import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Inbox, MessageCircle, Phone, ClipboardList, RefreshCw, Plus, X, Save,
  AlertCircle, Trash2, ChevronDown,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_META = {
  booking: { label: 'Booking', color: 'text-pink-300 bg-pink-500/15 border-pink-500/30', Icon: ClipboardList },
  application: { label: 'Applicant', color: 'text-purple-300 bg-purple-500/15 border-purple-500/30', Icon: ClipboardList },
  consent: { label: 'Consent', color: 'text-cyan-300 bg-cyan-500/15 border-cyan-500/30', Icon: ClipboardList },
  dm: { label: 'DM', color: 'text-amber-300 bg-amber-500/15 border-amber-500/30', Icon: MessageCircle },
  call: { label: 'Phone call', color: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/30', Icon: Phone },
  other: { label: 'Other', color: 'text-gray-300 bg-white/5 border-white/10', Icon: Inbox },
};

const STATUS_META = {
  new: { label: 'New', color: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
  in_progress: { label: 'In progress', color: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  done: { label: 'Done', color: 'bg-green-500/15 text-green-300 border-green-500/30' },
  archived: { label: 'Archived', color: 'bg-white/5 text-[#A7A3BA] border-white/10' },
};

const PRIORITY_META = {
  low: 'text-[#A7A3BA]',
  normal: 'text-[#F4F2FF]',
  high: 'text-[#FF7ABD]',
};


function authHeaders() {
  const token = localStorage.getItem('casa_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function authedFetch(path, init = {}) {
  return fetch(`${API_URL}${path}`, {
    ...init,
    headers: { ...authHeaders(), 'Content-Type': 'application/json', ...(init.headers || {}) },
  });
}


export function InboxPanel() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState({ source: 'all', status: 'all' });
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(null); // 'dm' | 'call' | null
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: '100' });
    if (filter.source !== 'all') params.set('source', filter.source);
    if (filter.status !== 'all') params.set('status', filter.status);
    const res = await authedFetch(`/api/admin/inbox?${params}`);
    if (res.ok) {
      const data = await res.json();
      setItems(data.items || []);
      setCounts(data.counts || {});
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="inbox-panel">
      <SummaryStrip counts={counts} totalShown={items.length} />

      <div className="flex flex-wrap items-center gap-2 justify-between bg-[#111022] border border-white/10 rounded-xl p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <FilterSelect
            label="Source" testId="inbox-filter-source"
            value={filter.source}
            onChange={(v) => setFilter((f) => ({ ...f, source: v }))}
            options={[
              { value: 'all', label: 'All sources' },
              { value: 'booking', label: 'Bookings' },
              { value: 'application', label: 'Applicants' },
              { value: 'dm', label: 'DM notes' },
              { value: 'call', label: 'Phone calls' },
              { value: 'consent', label: 'Consent' },
              { value: 'other', label: 'Other' },
            ]}
          />
          <FilterSelect
            label="Status" testId="inbox-filter-status"
            value={filter.status}
            onChange={(v) => setFilter((f) => ({ ...f, status: v }))}
            options={[
              { value: 'all', label: 'All status' },
              { value: 'new', label: 'New' },
              { value: 'in_progress', label: 'In progress' },
              { value: 'done', label: 'Done' },
              { value: 'archived', label: 'Archived' },
            ]}
          />
        </div>

        <div className="flex items-center gap-2">
          <button onClick={load} className="p-1.5 rounded-md bg-[#0B0A12] text-[#A7A3BA] hover:text-[#F4F2FF]" data-testid="inbox-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setCreating('dm')} className="text-xs px-3 py-1.5 rounded-md bg-amber-500/15 text-amber-300 flex items-center gap-1 hover:bg-amber-500/25" data-testid="inbox-add-dm">
            <Plus className="w-3 h-3" /> Log DM
          </button>
          <button onClick={() => setCreating('call')} className="text-xs px-3 py-1.5 rounded-md bg-emerald-500/15 text-emerald-300 flex items-center gap-1 hover:bg-emerald-500/25" data-testid="inbox-add-call">
            <Plus className="w-3 h-3" /> Log call
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-[#111022] border border-white/10 rounded-xl p-8 text-center" data-testid="inbox-empty">
          <Inbox className="w-8 h-8 mx-auto text-[#A7A3BA] mb-2" />
          <p className="text-[#A7A3BA] text-sm">No entries match the current filters.</p>
          <p className="text-xs text-[#A7A3BA]/60 mt-1">Bookings + applicants stream in here automatically.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="inbox-list">
          {items.map((item) => (
            <InboxRow
              key={item.id}
              item={item}
              expanded={openId === item.id}
              onToggle={() => setOpenId((id) => (id === item.id ? null : item.id))}
              onChanged={load}
            />
          ))}
        </ul>
      )}

      {creating && (
        <CreateEntryModal
          source={creating}
          onClose={() => setCreating(null)}
          onCreated={() => { setCreating(null); load(); }}
        />
      )}
    </div>
  );
}


function SummaryStrip({ counts, totalShown }) {
  const stats = [
    { label: 'New', val: counts.new || 0, color: 'text-yellow-300' },
    { label: 'In progress', val: counts.in_progress || 0, color: 'text-blue-300' },
    { label: 'Done', val: counts.done || 0, color: 'text-green-300' },
    { label: 'Archived', val: counts.archived || 0, color: 'text-[#A7A3BA]' },
    { label: 'Showing', val: totalShown, color: 'text-[#F4F2FF]' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
      {stats.map(({ label, val, color }) => (
        <div key={label} className="bg-[#111022] border border-white/10 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-[#A7A3BA]">{label}</p>
          <p className={`text-xl font-semibold ${color}`}>{val}</p>
        </div>
      ))}
    </div>
  );
}


function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="text-xs text-[#A7A3BA] flex items-center gap-2">
      <span className="hidden sm:inline">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-[#0B0A12] border border-white/10 rounded-lg px-2 py-1.5 text-xs text-[#F4F2FF]"
        data-testid={testId}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}


function InboxRow({ item, expanded, onToggle, onChanged }) {
  const meta = SOURCE_META[item.source] || SOURCE_META.other;
  const Icon = meta.Icon;

  return (
    <li className="bg-[#111022] border border-white/10 rounded-xl overflow-hidden" data-testid={`inbox-item-${item.id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-3 p-3 text-left hover:bg-white/[0.03]"
      >
        <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center border ${meta.color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`text-sm font-medium truncate ${PRIORITY_META[item.priority] || ''}`}>{item.title}</p>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_META[item.status]?.color || ''}`}>
              {STATUS_META[item.status]?.label || item.status}
            </span>
            {item.priority === 'high' && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FF4DA6]/15 text-[#FFB6D8] border border-[#FF4DA6]/30 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> High
              </span>
            )}
          </div>
          {item.summary && <p className="text-xs text-[#A7A3BA] mt-0.5 truncate">{item.summary}</p>}
          <p className="text-[10px] text-[#A7A3BA]/70 mt-1">
            {meta.label} · {new Date(item.created_at).toLocaleString()}
            {item.contact ? ` · ${item.contact}` : ''}
          </p>
        </div>
        <ChevronDown className={`w-4 h-4 text-[#A7A3BA] mt-1 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && <InboxRowDetails item={item} onChanged={onChanged} />}
    </li>
  );
}


function InboxRowDetails({ item, onChanged }) {
  const [status, setStatus] = useState(item.status);
  const [priority, setPriority] = useState(item.priority || 'normal');
  const [notes, setNotes] = useState(item.notes || '');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const dirty =
    status !== item.status ||
    priority !== (item.priority || 'normal') ||
    (notes || '') !== (item.notes || '');

  const save = async () => {
    setSaving(true);
    const res = await authedFetch(`/api/admin/inbox/${item.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status, priority, notes }),
    });
    if (res.ok) {
      setSavedAt(Date.now());
      onChanged();
    }
    setSaving(false);
  };

  const remove = async () => {
    if (!window.confirm('Remove this entry from the inbox?')) return;
    const res = await authedFetch(`/api/admin/inbox/${item.id}`, { method: 'DELETE' });
    if (res.ok) onChanged();
  };

  return (
    <div className="border-t border-white/5 p-3 bg-[#0B0A12]/60 space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-[#A7A3BA]">
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="form-input mt-1" data-testid={`inbox-status-${item.id}`}>
            {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </label>
        <label className="text-xs text-[#A7A3BA]">
          Priority
          <select value={priority} onChange={(e) => setPriority(e.target.value)} className="form-input mt-1" data-testid={`inbox-priority-${item.id}`}>
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
          </select>
        </label>
      </div>

      <label className="text-xs text-[#A7A3BA] block">
        Internal notes
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="form-input mt-1"
          placeholder="Anything to remember about this contact…"
          data-testid={`inbox-notes-${item.id}`}
        />
      </label>

      <div className="flex items-center justify-between">
        <button onClick={remove} className="text-xs px-3 py-1.5 rounded-lg bg-red-500/15 text-red-300 flex items-center gap-1 hover:bg-red-500/25" data-testid={`inbox-delete-${item.id}`}>
          <Trash2 className="w-3 h-3" /> Delete
        </button>
        <div className="flex items-center gap-2">
          {savedAt && <span className="text-xs text-green-300">Saved.</span>}
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="text-xs px-3 py-1.5 rounded-lg bg-[#27D7FF] text-[#07060B] font-medium flex items-center gap-1 disabled:opacity-40"
            data-testid={`inbox-save-${item.id}`}
          >
            <Save className="w-3 h-3" /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}


function CreateEntryModal({ source, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [priority, setPriority] = useState('normal');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const res = await authedFetch('/api/admin/inbox', {
      method: 'POST',
      body: JSON.stringify({ source, title, summary, name, contact, priority }),
    });
    setSaving(false);
    if (res.ok) onCreated();
  };

  const isCall = source === 'call';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="inbox-create-modal">
      <div className="bg-[#111022] border border-white/10 rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <h3 className="text-[#F4F2FF] font-semibold">{isCall ? 'Log a phone call' : 'Log a DM / inquiry'}</h3>
          <button onClick={onClose} className="text-[#A7A3BA] hover:text-[#F4F2FF]" data-testid="inbox-create-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <label className="text-xs text-[#A7A3BA] block">
            Title <span className="text-[#FF4DA6]">*</span>
            <input type="text" required value={title} onChange={(e) => setTitle(e.target.value)} className="form-input mt-1" placeholder={isCall ? 'Missed call from regular' : 'DM from Insta @handle'} data-testid="inbox-create-title" />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-[#A7A3BA] block">
              Name
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="form-input mt-1" data-testid="inbox-create-name" />
            </label>
            <label className="text-xs text-[#A7A3BA] block">
              {isCall ? 'Phone' : 'Handle / contact'}
              <input type="text" value={contact} onChange={(e) => setContact(e.target.value)} className="form-input mt-1" data-testid="inbox-create-contact" />
            </label>
          </div>
          <label className="text-xs text-[#A7A3BA] block">
            Summary
            <textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} className="form-input mt-1" placeholder="What happened, what they want, follow-up needed…" data-testid="inbox-create-summary" />
          </label>
          <label className="text-xs text-[#A7A3BA] block">
            Priority
            <select value={priority} onChange={(e) => setPriority(e.target.value)} className="form-input mt-1" data-testid="inbox-create-priority">
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-[#A7A3BA] hover:text-[#F4F2FF]">Cancel</button>
            <button type="submit" disabled={saving || !title.trim()} className="px-4 py-2 rounded-lg bg-[#FF4DA6] text-white text-sm font-medium disabled:opacity-40" data-testid="inbox-create-save">
              {saving ? 'Saving…' : 'Add to inbox'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


export default InboxPanel;
